import {
  Job,
  JobDocument,
  JobLocation,
  JobQuery,
  JobQueryVariables,
  LocaleEnum,
  Metadata,
  MetadataDocument,
  MetadataQuery,
  MetadataQueryVariables,
  PageEnum,
} from 'generated/apolloComponents'
import React from 'react'
import I18nProvider from 'next-translate/I18nProvider'
import useTranslation from 'next-translate/useTranslation'
import { addApolloState, initializeApollo } from '@/api/apollo'
import JobTemplate from '@/components/templates/Job'
import JobImmediateRedirect from '@/components/templates/JobImmediateRedirect'
import CustomHead from '@/components/molecules/CustomHead'
import commonEn from '../../../locales/en/common.json'
import commonPl from '../../../locales/pl/common.json'
import benefitPl from '../../../locales/pl/benefit.json'
import benefitEn from '../../../locales/en/benefit.json'
import companyEn from '../../../locales/en/company.json'
import companyPl from '../../../locales/pl/company.json'
import toolsEn from '../../../locales/en/tools.json'
import toolsPl from '../../../locales/pl/tools.json'
import hwmEn from '../../../locales/en/hwm.json'
import hwmPl from '../../../locales/pl/hwm.json'
import codeEn from '../../../locales/en/code.json'
import codePl from '../../../locales/pl/code.json'
import testEn from '../../../locales/en/test.json'
import testPl from '../../../locales/pl/test.json'
import jobsEn from '../../../locales/en/jobs.json'
import jobsPl from '../../../locales/pl/jobs.json'

interface IProps {
  metaData: Metadata
  data: {
    job: Job
  }
  defaultLocale: string
}

const Offer = ({ data, metaData, defaultLocale }: IProps) => {
  const { job } = data
  const { lang } = useTranslation()
  const locale = job.locale || lang
  const common = locale === 'en' ? commonEn : commonPl
  const benefit = locale === 'en' ? benefitEn : benefitPl
  const company = locale === 'en' ? companyEn : companyPl
  const tools = locale === 'en' ? toolsEn : toolsPl
  const hwm = locale === 'en' ? hwmEn : hwmPl
  const code = locale === 'en' ? codeEn : codePl
  const test = locale === 'en' ? testEn : testPl
  const jobs = locale === 'en' ? jobsEn : jobsPl

  return (
    <>
      <CustomHead metaData={metaData} />
      <I18nProvider
        lang={locale}
        namespaces={{ common, benefit, company, code, tools, hwm, test, jobs }}
      >
        {job.redirectTo ? (
          <JobImmediateRedirect job={job} />
        ) : (
          <JobTemplate job={job} defaultLocale={defaultLocale} />
        )}
      </I18nProvider>
    </>
  )
}

export const getServerSideProps = async (context: any) => {
  const apolloClient = initializeApollo({ ctx: context })

  const [jobData, metaData] = await Promise.all([
    apolloClient.query<JobQuery, JobQueryVariables>({
      query: JobDocument,
      variables: {
        id: context.params?.slug,
        viewToken: context.query?.viewToken,
      },
      errorPolicy: 'all',
    }),
    apolloClient.query<MetadataQuery, MetadataQueryVariables>({
      query: MetadataDocument,
      variables: {
        pageType: PageEnum.JobDetails,
        language: LocaleEnum[context.locale as 'pl' | 'en'],
        id: context.params?.slug,
      },
      errorPolicy: 'all',
    }),
  ])

  if (jobData.errors || metaData.errors) {
    if (jobData.errors && jobData.errors[0].message === 'MOVED_PERMANENTLY') {
      return {
        redirect: {
          destination: '/companies/jobs/',
          permanent: true,
        },
      }
    }
    return {
      notFound: true,
    }
  }

  if (jobData.data.job?.id !== context.params?.slug) {
    return {
      redirect: {
        destination: `/companies/jobs/${jobData.data.job?.id}`,
        permanent: true,
      },
    }
  }

  if (jobData.data.job?.locale === 'pl' && context.locale === 'en') {
    return {
      redirect: {
        destination: `https://${process.env.PL_HOST}/companies/jobs/${jobData.data.job?.id}`,
        permanent: true,
      },
    }
  }

  return addApolloState(apolloClient, {
    props: {
      metaData: metaData.data.metadata,
      data: jobData.data,
      defaultLocale: process.env.DEFAULT_LOCALE || 'pl',
    },
  })
}

export default Offer
